import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PathwayScenarioLight } from '@/types';
import {
  Inline,
  InlineItem,
  Input,
  Placeholder,
  Stack,
  useKeyboardShortcut,
} from '@kinesis/bungle';
import { shortcuts } from '@/shortcuts';
import { noop, isEmpty } from 'lodash/fp';
import Fuse from 'fuse.js';
import { Item } from './item';

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: true,
  minMatchCharLength: 0,
  keys: ['name', 'id'],
};

const PathwayScenariosList = (props: PathwayScenariosListProps) => {
  const { scenarios, onDelete = noop, now } = props;
  const navigate = useNavigate();
  const ref = useKeyboardShortcut({
    predicate: shortcuts.SEARCH_ITEMS.handler,
    callback: (event) => {
      event.preventDefault();
      ref.current?.focus();
    },
  });
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (isEmpty(search)) return scenarios;
    const fuse = new Fuse(scenarios, fuseOptions);
    const results = fuse.search(search);
    return results.map(({ item }) => item);
  }, [search, scenarios]);
  const onCancelSearch = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setSearch('');
      } else if (event.key === 'Escape') {
        ref.current?.blur();
      } else if (event.key === 'Enter') {
        if (filtered.length > 0) {
          navigate(`${filtered[0].id}`);
        }
      }
    },
    [ref, navigate, filtered],
  );

  return (
    <Stack space='medium'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Input
            ref={ref as any}
            type='search'
            placeholder={`Search (${shortcuts.SEARCH_ITEMS.mnemonic})`}
            onChange={setSearch}
            onKeyDown={onCancelSearch as any}
            value={search}
            magnitude='large'
            autoFocus
          />
        </InlineItem>
      </Inline>
      {filtered.map((scenario) => (
        <Item
          key={scenario.id}
          now={now}
          scenario={scenario}
          onDelete={onDelete}
        />
      ))}
      {isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no scenarios.</Placeholder>
      )}
      {!isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no matching scenarios.</Placeholder>
      )}
    </Stack>
  );
};

export type PathwayScenariosListProps = {
  now: string;
  scenarios: PathwayScenarioLight[];
  onDelete?: (scenario: number) => void;
};

export { PathwayScenariosList };
